import { useState, useEffect, useCallback } from "react";
import API, { formatWIB, formatWIBTime } from "../lib/api";
import { Heartbeat, Database, Globe, Table, Package, ArrowClockwise, CheckCircle, XCircle, CircleNotch, DownloadSimple, HardDrives, Cpu, CloudArrowUp, Wrench, Warning, Link as LinkIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function HealthCard({ icon: Icon, title, status, details, testId }) {
  const isOk = status === "connected" || status === "mock_connected" || status === "mock_active";
  return (
    <div data-testid={testId} className="border border-zinc-200 rounded-sm p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${isOk ? "bg-emerald-50" : "bg-red-50"}`}>
          <Icon size={18} className={isOk ? "text-emerald-600" : "text-red-600"} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${isOk ? "bg-emerald-500" : "bg-red-500"}`} />
            <span className="text-xs text-zinc-500 font-mono uppercase">{status}</span>
          </div>
        </div>
      </div>
      {details && (
        <div className="space-y-1">
          {Object.entries(details).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="text-zinc-400 font-mono">{k}</span>
              <span className="text-zinc-700 font-mono">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DepRow({ dep, onInstall, installing }) {
  const isInstalling = installing === dep.name;
  return (
    <div
      data-testid={`dep-${dep.name}`}
      className={`flex items-center justify-between py-3 px-4 border-b border-zinc-100 last:border-0 ${
        !dep.installed ? "bg-amber-50/50" : ""
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {dep.installed ? (
          <CheckCircle size={18} weight="fill" className="text-emerald-500 flex-shrink-0" />
        ) : (
          <XCircle size={18} weight="fill" className="text-red-400 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono font-semibold text-zinc-900">{dep.name}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
              dep.category === "system"
                ? "bg-blue-50 text-blue-600"
                : "bg-violet-50 text-violet-600"
            }`}>
              {dep.category}
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">{dep.description}</p>
          {dep.installed && dep.version && (
            <p className="text-[11px] font-mono text-zinc-400 mt-0.5">v{dep.version}</p>
          )}
        </div>
      </div>

      {!dep.installed && (
        <button
          data-testid={`install-${dep.name}`}
          onClick={() => onInstall(dep)}
          disabled={isInstalling}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors flex-shrink-0 ml-3 ${
            isInstalling
              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              : "bg-zinc-900 text-white hover:bg-zinc-700"
          }`}
        >
          {isInstalling ? (
            <>
              <CircleNotch size={14} className="animate-spin" />
              <span>Installing...</span>
            </>
          ) : (
            <>
              <DownloadSimple size={14} />
              <span>Install</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-mono ${highlight ? "text-red-600 font-semibold" : "text-zinc-800"}`}>{value ?? "-"}</span>
    </div>
  );
}

export default function HealthPage() {
  const [health, setHealth] = useState(null);
  const [deps, setDeps] = useState(null);
  const [serverInfo, setServerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depsLoading, setDepsLoading] = useState(true);
  const [installing, setInstalling] = useState(null);
  const [installResult, setInstallResult] = useState(null);

  // Data Health
  const [dataHealth, setDataHealth] = useState(null);
  const [dataHealthLoading, setDataHealthLoading] = useState(false);
  const [fixingJob, setFixingJob] = useState(null);
  const [fixResult, setFixResult] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const { data } = await API.get("/audit/health");
      setHealth(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadDeps = useCallback(async () => {
    setDepsLoading(true);
    try {
      const { data } = await API.get("/audit/system-deps");
      setDeps(data);
    } catch (e) { console.error(e); }
    finally { setDepsLoading(false); }
  }, []);

  const loadServerInfo = useCallback(async () => {
    try {
      const { data } = await API.get("/audit/server-info");
      setServerInfo(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadDataHealth = useCallback(async () => {
    setDataHealthLoading(true);
    try {
      const { data } = await API.get("/audit/data-health?days=60");
      setDataHealth(data);
    } catch (e) {
      console.error(e);
      if (e.response?.status !== 403) {
        setDataHealth({ error: e.response?.data?.detail || e.message });
      }
    }
    finally { setDataHealthLoading(false); }
  }, []);

  const applyFix = async (issue) => {
    setFixingJob(`${issue.job_id}-${issue.fix_action}`);
    setFixResult(null);
    try {
      const { data } = await API.post("/audit/data-health/fix", {
        job_id: issue.job_id,
        fix_action: issue.fix_action,
      });
      setFixResult({ job_id: issue.job_id, ...data });
      if (data.ok) {
        // Refresh scan
        setTimeout(() => loadDataHealth(), 500);
      }
    } catch (e) {
      setFixResult({ job_id: issue.job_id, error: e.response?.data?.detail || e.message });
    } finally {
      setFixingJob(null);
    }
  };

  useEffect(() => {
    loadHealth();
    loadDeps();
    loadServerInfo();
    loadDataHealth();
  }, [loadHealth, loadDeps, loadServerInfo, loadDataHealth]);

  const handleInstall = async (dep) => {
    setInstalling(dep.name);
    setInstallResult(null);
    try {
      const { data } = await API.post("/audit/install-dep", {
        name: dep.name,
        install_cmd: dep.install_cmd,
      });
      setInstallResult(data);
      if (data.success) {
        // Refresh deps after successful install
        setTimeout(() => loadDeps(), 1000);
      }
    } catch (e) {
      setInstallResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setInstalling(null);
    }
  };

  const refreshAll = () => {
    setLoading(true);
    setDepsLoading(true);
    setInstallResult(null);
    loadHealth();
    loadDeps();
    loadServerInfo();
  };

  if (loading && depsLoading) {
    return <div className="p-8 text-sm text-zinc-400 font-mono">Checking system health...</div>;
  }

  const missingCount = deps?.dependencies?.filter(d => !d.installed).length || 0;

  return (
    <div data-testid="health-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">System Health</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Last checked: {health?.timestamp ? formatWIB(health.timestamp) : "-"}
          </p>
        </div>
        <button
          data-testid="refresh-health-btn"
          onClick={refreshAll}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-600 transition-colors"
        >
          <ArrowClockwise size={14} />
          Refresh
        </button>
      </div>

      {/* Service Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <HealthCard
          icon={Database}
          title="MongoDB"
          status={health?.mongodb?.status || "unknown"}
          testId="health-mongodb"
        />
        <HealthCard
          icon={Globe}
          title="Panel Worker"
          status={health?.panel_worker?.status || "unknown"}
          details={{ is_mock: String(health?.panel_worker?.is_mock || false) }}
          testId="health-panel"
        />
        <HealthCard
          icon={Table}
          title="Sheets Connector"
          status={health?.sheets_connector?.status || "unknown"}
          details={{ is_mock: String(health?.sheets_connector?.is_mock || false) }}
          testId="health-sheets"
        />
      </div>

      {/* Data Health — Inconsistency Scanner */}
      <div data-testid="data-health-section" className="border border-zinc-200 rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50/50">
          <div className="flex items-center gap-2">
            <Wrench size={18} className="text-zinc-600" />
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Data Health — Inconsistency Scanner</h2>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Deteksi cache/data yang hilang atau tidak konsisten pada job yang sudah pernah di-verifikasi.
                {dataHealth?.scanned_at && (
                  <span className="ml-2 font-mono">
                    · Scan {dataHealth.scanned_jobs} job, terakhir {formatWIBTime(dataHealth.scanned_at)}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            data-testid="rescan-data-health-btn"
            onClick={loadDataHealth}
            disabled={dataHealthLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-600 transition-colors disabled:opacity-50"
          >
            <ArrowClockwise size={12} className={dataHealthLoading ? "animate-spin" : ""} />
            {dataHealthLoading ? "Scanning..." : "Rescan"}
          </button>
        </div>

        {dataHealth?.error && (
          <div className="p-4 text-xs text-red-600 bg-red-50">
            Error: {dataHealth.error}
          </div>
        )}

        {!dataHealth?.error && dataHealth && (
          <>
            {/* Summary bar */}
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
              {dataHealth.summary.total === 0 ? (
                <span className="text-xs text-emerald-700 font-mono flex items-center gap-1">
                  <CheckCircle size={14} weight="fill" /> Semua job sehat. Tidak ditemukan inkonsistensi.
                </span>
              ) : (
                <>
                  <span className="text-xs text-zinc-700 font-mono">
                    Ditemukan <strong>{dataHealth.summary.total}</strong> issue:
                  </span>
                  {dataHealth.summary.critical > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-red-100 text-red-700 font-mono font-semibold uppercase">
                      {dataHealth.summary.critical} Kritis
                    </span>
                  )}
                  {dataHealth.summary.warning > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700 font-mono font-semibold uppercase">
                      {dataHealth.summary.warning} Warning
                    </span>
                  )}
                  {Object.entries(dataHealth.summary.by_type).map(([t, n]) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 font-mono">
                      {t} × {n}
                    </span>
                  ))}
                </>
              )}
            </div>

            {/* Fix result banner */}
            {fixResult && (
              <div className={`px-4 py-2 text-xs font-mono flex items-center gap-2 ${
                fixResult.ok ? "bg-emerald-50 text-emerald-800 border-b border-emerald-100"
                : "bg-red-50 text-red-800 border-b border-red-100"
              }`}>
                {fixResult.ok ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />}
                <span>Job {fixResult.job_id?.slice(0, 8)}: {fixResult.message || fixResult.error}</span>
                <button onClick={() => setFixResult(null)} className="ml-auto text-[10px] underline">Tutup</button>
              </div>
            )}

            {/* Issues table */}
            {dataHealth.issues.length > 0 && (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Severity</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Job</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Type</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Detail</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {dataHealth.issues.map((issue, i) => {
                    const fixKey = `${issue.job_id}-${issue.fix_action}`;
                    const isFixing = fixingJob === fixKey;
                    return (
                      <tr key={i} data-testid={`data-health-issue-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="px-3 py-2">
                          <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${
                            issue.severity === "critical" ? "bg-red-100 text-red-700"
                            : issue.severity === "warning" ? "bg-amber-100 text-amber-700"
                            : "bg-zinc-100 text-zinc-600"
                          }`}>{issue.severity}</span>
                        </td>
                        <td className="px-3 py-2 text-[11px] font-mono">
                          <Link to={`/jobs/${issue.job_id}`} className="text-violet-600 hover:underline flex items-center gap-1">
                            <LinkIcon size={10} /> {issue.site_name || "?"} · {issue.job_id?.slice(0, 8)}
                          </Link>
                          <div className="text-[9px] text-zinc-400">{issue.date_from} → {issue.date_to}</div>
                        </td>
                        <td className="px-3 py-2 text-[10px] font-mono text-zinc-700">{issue.type}</td>
                        <td className="px-3 py-2 text-[10px] text-zinc-600 max-w-md">{issue.detail}</td>
                        <td className="px-3 py-2 text-right">
                          {issue.fix_action && (
                            <button
                              data-testid={`fix-issue-${i}`}
                              onClick={() => applyFix(issue)}
                              disabled={isFixing}
                              className="text-[10px] px-2 py-1 bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm font-mono disabled:opacity-50 flex items-center gap-1"
                            >
                              {isFixing ? <CircleNotch size={10} className="animate-spin" /> : <Wrench size={10} />}
                              {isFixing ? "Memperbaiki..." : (issue.fix_label || "Fix")}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Server Information */}
      {serverInfo && (
        <div data-testid="server-info-section" className="border border-zinc-200 rounded-sm">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-zinc-50/50">
            <HardDrives size={18} className="text-zinc-600" />
            <h2 className="text-sm font-semibold text-zinc-900">Server Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-zinc-100">
            {/* Server */}
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-1.5 mb-2">
                <Globe size={14} className="text-zinc-400" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Server</span>
              </div>
              <InfoRow label="Hostname" value={serverInfo.server?.hostname} />
              <InfoRow label="IP Address" value={serverInfo.server?.local_ip} />
              <InfoRow label="OS" value={serverInfo.server?.os} />
              <InfoRow label="Architecture" value={serverInfo.server?.arch} />
            </div>
            {/* Runtime */}
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-1.5 mb-2">
                <CloudArrowUp size={14} className="text-zinc-400" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Runtime</span>
              </div>
              <InfoRow label="Python" value={serverInfo.runtime?.python} />
              <InfoRow label="FastAPI" value={serverInfo.runtime?.fastapi} />
              <InfoRow label="Playwright" value={serverInfo.runtime?.playwright} />
              <InfoRow label="Uptime" value={serverInfo.runtime?.uptime} />
              <InfoRow label="PID" value={serverInfo.runtime?.pid} />
            </div>
            {/* Resources */}
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-1.5 mb-2">
                <Cpu size={14} className="text-zinc-400" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Resources</span>
              </div>
              <InfoRow label="CPU Cores" value={serverInfo.resources?.cpu_cores} />
              <InfoRow label="CPU Usage" value={serverInfo.resources?.cpu_usage} highlight={parseFloat(serverInfo.resources?.cpu_usage) > 80} />
              <InfoRow label="Memory" value={`${serverInfo.resources?.memory_used} / ${serverInfo.resources?.memory_total}`} />
              <InfoRow label="Memory Usage" value={serverInfo.resources?.memory_percent} highlight={parseFloat(serverInfo.resources?.memory_percent) > 80} />
              <InfoRow label="Disk" value={`${serverInfo.resources?.disk_used} / ${serverInfo.resources?.disk_total}`} />
              <InfoRow label="Disk Usage" value={serverInfo.resources?.disk_percent} highlight={parseFloat(serverInfo.resources?.disk_percent) > 80} />
            </div>
            {/* Database */}
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-1.5 mb-2">
                <Database size={14} className="text-zinc-400" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Database</span>
              </div>
              <InfoRow label="MongoDB" value={`v${serverInfo.database?.mongodb_version}`} />
              <InfoRow label="DB Size" value={serverInfo.database?.db_size} />
              <InfoRow label="Collections" value={serverInfo.database?.collections} />
            </div>
          </div>
        </div>
      )}

      {health?.jobs && (
        <div className="border border-zinc-200 rounded-sm p-4">
          <h2 className="text-sm font-semibold text-zinc-900 mb-3">Job Queue Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Pending Jobs</p>
              <p className="text-2xl font-mono font-semibold text-zinc-900">{health.jobs.pending}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Running Jobs</p>
              <p className="text-2xl font-mono font-semibold text-zinc-900">{health.jobs.running}</p>
            </div>
          </div>
        </div>
      )}

      {/* System Dependencies Section */}
      <div data-testid="system-deps-section" className="border border-zinc-200 rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50/50">
          <div className="flex items-center gap-2">
            <Package size={18} className="text-zinc-600" />
            <h2 className="text-sm font-semibold text-zinc-900">System Dependencies</h2>
            {deps?.summary && (
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${
                deps.summary.all_ok
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}>
                {deps.summary.installed}/{deps.summary.total} installed
              </span>
            )}
          </div>
          {missingCount > 0 && (
            <span className="text-[10px] font-mono text-red-500 bg-red-50 px-2 py-0.5 rounded-sm">
              {missingCount} missing
            </span>
          )}
        </div>

        {depsLoading ? (
          <div className="p-6 text-center">
            <CircleNotch size={20} className="animate-spin text-zinc-300 mx-auto mb-2" />
            <p className="text-xs text-zinc-400 font-mono">Checking dependencies...</p>
          </div>
        ) : (
          <div>
            {deps?.dependencies?.map((dep) => (
              <DepRow key={dep.name} dep={dep} onInstall={handleInstall} installing={installing} />
            ))}
          </div>
        )}

        {/* Install Result Toast */}
        {installResult && (
          <div
            data-testid="install-result"
            className={`mx-4 mb-4 p-3 rounded-sm text-xs font-mono ${
              installResult.success
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {installResult.success ? (
                <CheckCircle size={14} weight="fill" className="text-emerald-500" />
              ) : (
                <XCircle size={14} weight="fill" className="text-red-500" />
              )}
              <span className="font-semibold">
                {installResult.success ? "Berhasil diinstall" : "Gagal install"}
              </span>
            </div>
            {installResult.error && (
              <pre className="text-[10px] whitespace-pre-wrap mt-1 opacity-80">{installResult.error}</pre>
            )}
            {installResult.output && installResult.success && (
              <pre className="text-[10px] whitespace-pre-wrap mt-1 opacity-70 max-h-20 overflow-y-auto">{installResult.output}</pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
